import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import Loading from '../components/Loading';
import { DashboardData, LoyaltyPlanType } from '../types';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusString = (status: unknown): 'Pending' | 'Approved' | 'Rejected' => {
    if (typeof status === 'string') {
      return status as 'Pending' | 'Approved' | 'Rejected';
    }
    if (typeof status === 'number') {
      // C# enum: Pending = 0, Approved = 1, Rejected = 2
      const statusMap: Record<number, 'Pending' | 'Approved' | 'Rejected'> = {
        0: 'Pending',
        1: 'Approved',
        2: 'Rejected'
      };
      return statusMap[status] || 'Pending';
    }
    return 'Pending'; // default fallback
  };

  const loadDashboard = async () => {
    try {
      const response = await api.get<DashboardData>('/admin/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading message="Carregando painel..." />;
  }

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Visão geral do seu programa de fidelidade</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <div className="stat-value">{dashboard?.totalEnrolledClients || 0}</div>
            <div className="stat-label">Clientes Cadastrados</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <div className="stat-value">{dashboard?.pendingTransactions || 0}</div>
            <div className="stat-label">Transações Pendentes</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <div className="stat-value">{dashboard?.transactionsThisMonth || 0}</div>
            <div className="stat-label">Transações Este Mês</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">⭐</div>
          <div className="stat-content">
            <div className="stat-value">{dashboard?.totalPointsDistributed || 0}</div>
            <div className="stat-label">Pontos Distribuídos</div>
          </div>
        </div>
      </div>

      {dashboard?.activePlan && (
        <div className="section">
          <h2>Plano Ativo</h2>
          <div className="plan-card">
            <h3>{dashboard.activePlan.name}</h3>
            <p>{dashboard.activePlan.description}</p>
            <div className="plan-details">
              <span>
                Tipo: {dashboard.activePlan.type === LoyaltyPlanType.ByPoints ? 'Por Pontuação' : 'Por Utilização'}
              </span>
              <span>Meta: {dashboard.activePlan.targetPoints} pontos</span>
              <span>Recompensa: {dashboard.activePlan.rewardDescription}</span>
            </div>
          </div>
        </div>
      )}

      {dashboard?.recentTransactions && dashboard.recentTransactions.length > 0 && (
        <div className="section">
          <h2>Transações Pendentes</h2>
          <div className="transactions-list">
            {dashboard.recentTransactions.map((transaction) => {
              const status = getStatusString(transaction.status);
              return (
                <div key={transaction.transactionId} className="transaction-item">
                  <div className="transaction-info">
                    <div className="transaction-client">{transaction.clientName}</div>
                    <div className="transaction-amount">R$ {transaction.amount.toFixed(2)}</div>
                  </div>
                  <div className="transaction-meta">
                    <span className="transaction-points">{transaction.pointsEarned} pts</span>
                    <span className={`transaction-status ${status.toLowerCase()}`}>
                      {status === 'Pending' ? 'Pendente' : status === 'Approved' ? 'Aprovado' : 'Rejeitado'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;


